<template>
  <v-dialog :origin="dialogOrigin" persistent fullscreen :value="active" :close-on-back="closeOnBack" content-class="auth-dialog">
    <v-card dark flat class="auth-shell auth-shell--register">
      <div class="auth-register-layout">
        <v-btn icon class="auth-close-button" aria-label="Registrierung schliessen" @click="$emit('close')">
          <v-icon aria-hidden="true">mdi-close</v-icon>
        </v-btn>

        <aside class="auth-register-rail" aria-label="Fortschritt der Registrierung">
          <div class="auth-register-brand">
            <img class="auth-brand-logo" :src="brandLogo" alt="WELL COME FIT" width="1200" height="931" />
          </div>

          <div class="auth-register-rail__intro">
            <span>WELL COME FIT App</span>
            <h2>Dein Account.<br />Schritt für Schritt.</h2>
          </div>

          <ol class="auth-step-list">
            <li
              v-for="(step, index) in registrationSteps"
              :key="step.title"
              :class="{ 'is-active': registrationFormStepper === index + 1, 'is-complete': registrationFormStepper > index + 1 }"
              :aria-current="registrationFormStepper === index + 1 ? 'step' : null">
              <span class="auth-step-list__number">
                <v-icon v-if="registrationFormStepper > index + 1" aria-hidden="true">mdi-check</v-icon>
                <template v-else>{{ index + 1 }}</template>
              </span>
              <span>
                <strong>{{ step.title }}</strong>
                <small>{{ step.hint }}</small>
              </span>
            </li>
          </ol>

          <div class="auth-register-rail__foot">
            <v-icon aria-hidden="true">mdi-shield-check-outline</v-icon>
            <span>Deine Angaben werden verschlüsselt übertragen und sicher verarbeitet.</span>
          </div>
        </aside>

        <main ref="registrationMain" class="auth-register-main">
          <header class="auth-register-topbar">
            <img class="auth-register-topbar__mobile-logo" :src="brandLogo" alt="WELL COME FIT" width="1200" height="931" />
            <div class="auth-register-progress">
              <div class="auth-register-progress__meta">
                <strong>Schritt {{ registrationFormStepper }} von {{ registrationSteps.length }}</strong>
                <span>{{ currentStepTitle }}</span>
              </div>
              <div
                class="auth-register-progress__track"
                role="progressbar"
                aria-label="Fortschritt der Registrierung"
                aria-valuemin="1"
                :aria-valuemax="registrationSteps.length"
                :aria-valuenow="registrationFormStepper">
                <div class="auth-register-progress__bar" :style="{ width: registrationProgress + '%' }"></div>
              </div>
            </div>
          </header>

          <v-form ref="registerForm" class="auth-register-form" lazy-validation novalidate @submit.prevent>
            <v-window v-model="registrationFormStepper" :touch="() => {}" class="auth-register-window">
              <v-window-item :value="1">
                <section class="auth-step">
                  <header class="auth-step-header">
                    <span class="auth-step-header__count">Los geht's</span>
                    <h1>Dein Zugang zur App.</h1>
                    <p>Starte mit deiner E-Mail-Adresse. Sie wird später für die Anmeldung verwendet.</p>
                  </header>

                  <v-text-field
                    v-model.trim="formData.email"
                    class="auth-field"
                    outlined
                    color="primary"
                    type="email"
                    inputmode="email"
                    name="register-email"
                    autocomplete="email"
                    label="E-Mail-Adresse"
                    placeholder="name@beispiel.ch"
                    prepend-inner-icon="mdi-email-outline"
                    :rules="rules.email"
                    :error-messages="emailAlreadyUsed ? 'Diese E-Mail-Adresse ist bereits vergeben.' : emailValidationError"
                    :loading="validatingEmail === true"
                    hide-details="auto"
                    required
                    autofocus
                    @input="handleEmailInput"
                    @blur="validateEmail"></v-text-field>

                  <v-expand-transition>
                    <div v-if="validatingEmail === 'validated'">
                      <div class="auth-email-status">
                        <v-icon aria-hidden="true">mdi-check-circle-outline</v-icon>
                        <span>Die E-Mail-Adresse ist verfügbar. Jetzt fehlt nur noch dein Passwort.</span>
                      </div>

                      <v-text-field
                        v-if="requirePassword || forcePassword"
                        v-model="formData.passwort"
                        class="auth-field"
                        outlined
                        color="primary"
                        label="Passwort"
                        name="register-password"
                        autocomplete="new-password"
                        prepend-inner-icon="mdi-lock-outline"
                        :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                        :type="showPassword ? 'text' : 'password'"
                        :rules="rules.password"
                        hide-details="auto"
                        @click:append="showPassword = !showPassword"></v-text-field>

                      <template v-if="!requirePassword">
                        <v-alert text dense type="info" class="auth-inline-alert">
                          Der Kunde erhält per E-Mail einen sicheren Link, über den das Passwort festgelegt werden kann.
                        </v-alert>
                        <v-checkbox v-model="forcePassword" color="primary" label="Passwort jetzt direkt setzen" hide-details></v-checkbox>
                      </template>
                    </div>
                  </v-expand-transition>
                </section>
              </v-window-item>

              <v-window-item :value="2">
                <section class="auth-step">
                  <header class="auth-step-header">
                    <span class="auth-step-header__count">Persönlich</span>
                    <h1>Wie dürfen wir dich nennen?</h1>
                    <p>Mit deinem Vornamen wird dein App-Erlebnis persönlicher.</p>
                  </header>

                  <v-text-field
                    v-if="registrationFormStepper === 2"
                    v-model.trim="formData.vorname"
                    class="auth-field"
                    outlined
                    color="primary"
                    type="text"
                    name="given-name"
                    autocomplete="given-name"
                    label="Vorname"
                    prepend-inner-icon="mdi-account-outline"
                    :rules="rules.required"
                    hide-details="auto"
                    autofocus></v-text-field>
                </section>
              </v-window-item>

              <v-window-item :value="3">
                <section class="auth-step">
                  <header class="auth-step-header">
                    <span class="auth-step-header__count">Fast bekannt</span>
                    <h1>Und dein Nachname?</h1>
                    <p>Alles klar, {{ formData.vorname }}. Ergänze jetzt noch deinen Familiennamen.</p>
                  </header>

                  <v-text-field
                    v-if="registrationFormStepper === 3"
                    v-model.trim="formData.nachname"
                    class="auth-field"
                    outlined
                    color="primary"
                    type="text"
                    name="family-name"
                    autocomplete="family-name"
                    label="Nachname"
                    prepend-inner-icon="mdi-account-outline"
                    :rules="rules.required"
                    hide-details="auto"
                    autofocus></v-text-field>
                </section>
              </v-window-item>

              <v-window-item :value="4">
                <section class="auth-step">
                  <header class="auth-step-header">
                    <span class="auth-step-header__count">Kontaktdaten</span>
                    <h1>Wo erreichen wir dich?</h1>
                    <p>Diese Angaben helfen deinem Studio, deinen Account korrekt zuzuordnen.</p>
                  </header>

                  <div v-if="registrationFormStepper === 4" class="auth-field-grid">
                    <v-text-field
                      v-model.trim="formData.adresse_1"
                      class="auth-field auth-field-grid__full"
                      outlined
                      color="primary"
                      type="text"
                      name="street-address"
                      autocomplete="street-address"
                      label="Adresse"
                      prepend-inner-icon="mdi-map-marker-outline"
                      :rules="rules.required"
                      hide-details="auto"
                      autofocus></v-text-field>
                    <v-text-field
                      v-model.number="formData.plz"
                      class="auth-field"
                      outlined
                      color="primary"
                      inputmode="numeric"
                      name="postal-code"
                      autocomplete="postal-code"
                      label="PLZ"
                      :rules="rules.isNumber"
                      hide-details="auto"></v-text-field>
                    <v-text-field
                      v-model.trim="formData.stadt"
                      class="auth-field"
                      outlined
                      color="primary"
                      type="text"
                      name="address-level2"
                      autocomplete="address-level2"
                      label="Ort"
                      :rules="rules.required"
                      hide-details="auto"></v-text-field>
                    <v-text-field
                      v-model.trim="formData.land"
                      class="auth-field"
                      outlined
                      color="primary"
                      type="text"
                      name="country-name"
                      autocomplete="country-name"
                      label="Land"
                      :rules="rules.isString"
                      hide-details="auto"></v-text-field>
                    <v-text-field
                      v-model.trim="formData.telefon"
                      class="auth-field"
                      outlined
                      color="primary"
                      type="tel"
                      inputmode="tel"
                      name="tel"
                      autocomplete="tel"
                      label="Telefon"
                      :rules="rules.telefon"
                      hide-details="auto"></v-text-field>
                  </div>
                </section>
              </v-window-item>

              <v-window-item :value="5">
                <section class="auth-step">
                  <header class="auth-step-header">
                    <span class="auth-step-header__count">Dein Profil</span>
                    <h1>Ein paar Details zu dir.</h1>
                    <p>Geburtsdatum und Geschlecht werden für dein persönliches Profil benötigt.</p>
                  </header>

                  <div v-if="registrationFormStepper === 5">
                    <div class="auth-gender-grid" role="group" aria-label="Geschlecht auswählen">
                      <button
                        type="button"
                        class="auth-gender-card"
                        :class="{ 'is-selected': formData.geschlecht === 'm' }"
                        :aria-pressed="formData.geschlecht === 'm'"
                        @click="formData.geschlecht = 'm'">
                        <v-icon aria-hidden="true">mdi-gender-male</v-icon>
                        <span>Männlich</span>
                      </button>
                      <button
                        type="button"
                        class="auth-gender-card"
                        :class="{ 'is-selected': formData.geschlecht === 'w' }"
                        :aria-pressed="formData.geschlecht === 'w'"
                        @click="formData.geschlecht = 'w'">
                        <v-icon aria-hidden="true">mdi-gender-female</v-icon>
                        <span>Weiblich</span>
                      </button>
                    </div>

                    <v-expand-transition>
                      <div v-if="formData.geschlecht" class="auth-field-grid">
                        <v-text-field
                          v-model="formData.geburtstag"
                          class="auth-field auth-field-grid__full"
                          outlined
                          color="primary"
                          type="date"
                          name="bday"
                          autocomplete="bday"
                          label="Geburtsdatum"
                          prepend-inner-icon="mdi-cake-variant-outline"
                          :rules="rules.required"
                          hide-details="auto"></v-text-field>
                        <v-text-field
                          v-model.number="formData.gewicht"
                          class="auth-field"
                          outlined
                          color="primary"
                          type="number"
                          inputmode="decimal"
                          label="Gewicht (optional)"
                          suffix="kg"
                          hide-details="auto"></v-text-field>
                        <v-text-field
                          v-model.number="formData.groesse"
                          class="auth-field"
                          outlined
                          color="primary"
                          type="number"
                          inputmode="decimal"
                          label="Grösse (optional)"
                          suffix="cm"
                          hide-details="auto"></v-text-field>
                      </div>
                    </v-expand-transition>
                  </div>
                </section>
              </v-window-item>

              <v-window-item :value="6">
                <section class="auth-step">
                  <header class="auth-step-header">
                    <span class="auth-step-header__count">Abschluss</span>
                    <h1>Nur noch bestätigen.</h1>
                    <p>Prüfe die Hinweise und vervollständige deine Einwilligung.</p>
                  </header>

                  <div v-if="registrationFormStepper === 6">
                    <div class="auth-consent-panel">
                      <consent-declaration-card
                        :age="$moment().diff($moment(formData.geburtstag), 'years')"
                        darkMode
                        @savedSignature="saveSignature"></consent-declaration-card>
                    </div>

                    <div class="auth-consent-panel">
                      <h3>Haftungsausschluss</h3>
                      <p>
                        *Haftungsausschluss. Zu Risiken und Nebenwirkungen frage doch immer Deinen Arzt oder Apotheker! Das ist zwar lustig
                        formuliert, bringt es aber auf den Punkt. Wir möchten Dich bitten vorab von medizinischer Seite abklären zu lassen, ob Du fit
                        für ein Fitnesstraining bist. Wir übernehmen keine Haftung.
                      </p>
                      <v-checkbox
                        v-model="formData.rulesAccepted"
                        color="primary"
                        label="Ich habe den Haftungsausschluss gelesen und akzeptiere ihn."
                        :rules="rules.required"
                        hide-details="auto"></v-checkbox>
                    </div>

                    <div class="auth-consent-panel">
                      <h3>Auf dem Laufenden bleiben</h3>
                      <p>Erhalte auf Wunsch Neuigkeiten und Angebote aus deinem Center.</p>
                      <v-checkbox v-model="formData.newsletterAccepted" color="primary" label="Newsletter abonnieren" hide-details></v-checkbox>
                      <v-select
                        v-if="formData.newsletterAccepted"
                        v-model="formData.newsletterCenter"
                        :items="center"
                        class="auth-field mt-4"
                        outlined
                        color="primary"
                        label="Center"
                        :menu-props="{ contentClass: 'auth-select-menu' }"
                        prepend-inner-icon="mdi-map-marker-outline"
                        hide-details="auto"></v-select>
                    </div>
                  </div>
                </section>
              </v-window-item>
            </v-window>

            <v-expand-transition>
              <v-alert v-if="submissionError" type="error" text dense class="auth-inline-alert auth-register-alert" role="alert">
                {{ submissionError }}
              </v-alert>
            </v-expand-transition>

            <footer class="auth-register-actions">
              <span v-if="showModeSwitch && registrationFormStepper === 1" class="auth-register-login-copy">
                Schon dabei?
                <button type="button" class="auth-text-button" @click="$emit('show-login')">Anmelden</button>
              </span>
              <v-btn v-if="registrationFormStepper > 1" depressed class="auth-secondary-button" :disabled="isSubmitting" @click="previousStep">
                <v-icon left aria-hidden="true">mdi-arrow-left</v-icon>
                Zurück
              </v-btn>
              <v-btn
                depressed
                class="auth-primary-button"
                :loading="isSubmitting"
                :disabled="!showNextButton || !stepFinished || isSubmitting"
                @click="nextStep">
                {{ registrationFormStepper === 6 ? "Account erstellen" : "Weiter" }}
                <v-icon right aria-hidden="true">{{ registrationFormStepper === 6 ? "mdi-check" : "mdi-arrow-right" }}</v-icon>
              </v-btn>
            </footer>
          </v-form>
        </main>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import "./authTheme.scss"
import { brandLogo } from "@/constants/brandAssets"
import api from "../../api"
import { EventBus } from "../../event-bus"
import consentDeclarationCard from "../../components/consentDeclaration/consentDeclarationCard.vue"

export default {
  name: "AuthRegister",
  components: {
    consentDeclarationCard,
  },
  props: {
    dialogOrigin: {
      type: String,
      default: "50% 50%",
    },
    autoLogin: {
      type: Boolean,
      default: true,
    },
    active: {
      type: Boolean,
      default: true,
    },
    closeOnBack: {
      type: Boolean,
      default: true,
    },
    requirePassword: {
      type: Boolean,
      default: true,
    },
    showModeSwitch: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({}),
    },
    resetOnOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      brandLogo,
      forcePassword: false,
      showPassword: false,
      emailAlreadyUsed: false,
      emailValidationError: "",
      emailValidationRequestId: 0,
      validatingEmail: false,
      isSubmitting: false,
      submissionError: "",
      registrationFormStepper: 1,
      registrationSteps: [
        { title: "Zugang", hint: "E-Mail & Passwort" },
        { title: "Vorname", hint: "Persönliche Ansprache" },
        { title: "Nachname", hint: "Dein vollständiger Name" },
        { title: "Kontakt", hint: "Adresse & Telefon" },
        { title: "Profil", hint: "Basisangaben" },
        { title: "Bestätigung", hint: "Einwilligung & Abschluss" },
      ],
      formData: {},
      center: [
        "Arbon",
        "Basel",
        "Bülach",
        "Dietlikon",
        "Frauenfeld",
        "Frauenfeld West",
        "Herisau",
        "Kreuzlingen",
        "Muri",
        "Netstal",
        "Niederurnen",
        "Obfelden",
        "Oerlikon",
        "Pfungen",
        "Rikon",
        "Schwanden",
        "Sirnach",
        "St. Gallen Einstein",
        "St. Gallen Ost",
        "St. Gallen West",
        "Steckborn",
        "Wallisellen",
        "Wetzikon",
        "Winterthur",
      ],
      rules: {
        telefon: [(v) => /^\+?[0-9]{7,15}$/.test(v) || "Bitte gib eine gültige Telefonnummer ein."],
        email: [(v) => !!v || "Die E-Mail wird benötigt", (v) => /^[^@]+@[^@]+\.[^@]+$/.test(v) || "Das ist keine gültige E-Mail"],
        password: [(v) => !!v || "Bitte gib dein Passwort ein.", (v) => (v && v.length >= 8) || "Das Passwort muss mindestens 8 Zeichen lang sein."],
        isString: [
          (v) => !!v || "Dieses Feld wird benötigt.",
          (v) => /^[a-zA-Z\u00C0-\u024F ]+$/.test(v) || "Dieses Feld sollte keine Zahlen enthalten.",
        ],
        isNumber: [(v) => !!v || "Dieses Feld wird benötigt.", (v) => /^\d*$/.test(v) || "Bitte gib eine Zahl ein."],
        isNumberNotRequired: [(v) => /^\d*\.?\d+$/.test(v) || "Bitte gib eine Zahl ein."],
        required: [(v) => !!v || "Dieses Feld wird benötigt."],
      },
    }
  },
  created() {
    this.setRegisterForm()
    this.applyInitialData()
  },
  mounted() {
    if (this.active && this.formData.email) this.$nextTick(() => this.validateEmail())
  },
  computed: {
    registrationProgress() {
      return Math.round((this.registrationFormStepper / this.registrationSteps.length) * 100)
    },
    currentStepTitle() {
      const step = this.registrationSteps[this.registrationFormStepper - 1]
      return step ? step.title : ""
    },
    showNextButton() {
      if (this.registrationFormStepper === 6) return Boolean(this.formData.rulesAccepted)
      return this.validatingEmail === "validated"
    },
    stepFinished() {
      if (this.registrationFormStepper === 1) {
        if (!this.formData.email || this.validatingEmail !== "validated") return false
        if ((this.requirePassword || this.forcePassword) && !this.formData.passwort) return false
      }
      if (this.registrationFormStepper === 2 && !this.formData.vorname) return false
      if (this.registrationFormStepper === 3 && !this.formData.nachname) return false
      if (this.registrationFormStepper === 4 && (!this.formData.adresse_1 || !this.formData.plz || !this.formData.stadt || !this.formData.telefon)) {
        return false
      }
      if (this.registrationFormStepper === 5 && (!this.formData.geschlecht || !this.formData.geburtstag)) return false
      if (this.registrationFormStepper === 6 && (!this.formData.rulesAccepted || !this.formData.consentDeclarationSignature)) return false

      const form = this.$refs.registerForm
      return form ? form.validate() : false
    },
  },
  watch: {
    active(value, previousValue) {
      if (value && !previousValue && this.resetOnOpen) this.prepareRegisterForOpen()
    },
    initialData: {
      deep: true,
      handler() {
        if (this.active) this.applyInitialData()
      },
    },
    registrationFormStepper() {
      this.submissionError = ""
      this.$nextTick(() => {
        const container = this.$refs.registrationMain
        if (!container || typeof container.scrollTo !== "function") return
        const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
        container.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" })
      })
    },
  },
  methods: {
    normalizePrefillPhone(value) {
      const raw = String(value || "").trim()
      if (!raw) return null
      const digits = raw.replace(/\D/g, "")
      return `${raw.startsWith("+") ? "+" : ""}${digits}` || null
    },
    applyInitialData() {
      const source = this.initialData || {}
      const prefill = {
        email: String(source.email || "").trim().toLowerCase(),
        vorname: String(source.vorname || source.firstName || "").trim(),
        nachname: String(source.nachname || source.lastName || "").trim(),
        telefon: this.normalizePrefillPhone(source.telefon || source.phone),
      }

      Object.entries(prefill).forEach(([field, value]) => {
        if (value && !this.formData[field]) this.$set(this.formData, field, value)
      })
    },
    prepareRegisterForOpen() {
      this.registrationFormStepper = 1
      this.forcePassword = false
      this.showPassword = false
      this.emailAlreadyUsed = false
      this.emailValidationError = ""
      this.emailValidationRequestId++
      this.validatingEmail = false
      this.isSubmitting = false
      this.submissionError = ""
      this.setRegisterForm()
      this.applyInitialData()
      this.$nextTick(() => {
        this.resetValidation()
        if (this.formData.email) this.validateEmail()
      })
    },
    saveSignature(signature) {
      this.formData.consentDeclarationSignature = signature
    },
    resetValidation() {
      if (this.$refs.registerForm) this.$refs.registerForm.resetValidation()
    },
    handleEmailInput() {
      this.emailValidationRequestId++
      this.emailAlreadyUsed = false
      this.emailValidationError = ""
      this.validatingEmail = false
    },
    async register() {
      if (this.isSubmitting || !this.$refs.registerForm.validate()) return

      this.submissionError = ""
      this.formData.requirePassword = this.forcePassword ? true : this.requirePassword
      if (this.$route.params.eventId) this.formData.eventId = this.$route.params.eventId
      this.formData.geburtstag = this.$moment(this.formData.geburtstag).format("YYYY-MM-DD")
      this.isSubmitting = true

      try {
        const data = await api.registerUser(this.formData)
        if (this.$route.params.eventId) data.eventId = this.$route.params.eventId

        if (this.autoLogin) {
          EventBus.$emit("login", data)
        } else {
          this.$emit("register", data)
        }

        this.setRegisterForm()
        this.registrationFormStepper = 1
        this.forcePassword = false
        this.showPassword = false
        this.emailAlreadyUsed = false
        this.validatingEmail = false
      } catch (error) {
        const response = error && error.response
        const errorData = response && response.data

        if (errorData && (errorData.status === 3 || errorData.code === 11000)) {
          this.submissionError = this.autoLogin
            ? "Diese E-Mail-Adresse ist bereits registriert. Bitte melde dich stattdessen an."
            : "Diese E-Mail-Adresse ist bereits registriert. Bitte suche nach dem bestehenden Kundenkonto."
        } else if (errorData === "Mandant nicht gefunden") {
          this.submissionError = "Die Registrierung ist unter dieser App-Adresse nicht verfügbar. Bitte wende dich an dein Center."
        } else if (errorData === "error" || (errorData && errorData.code === "REGISTRATION_INPUT_MISSING")) {
          this.submissionError = "E-Mail-Adresse oder Passwort fehlen. Bitte gehe zurück zum Schritt Zugang und ergänze deine Angaben."
        } else if (!response && error && error.request) {
          this.submissionError = "Es konnte keine Serverantwort empfangen werden. Bitte prüfe deine Internetverbindung und versuche es erneut."
        } else if (response && response.status >= 500) {
          this.submissionError = "Bei der Registrierung ist ein Serverfehler aufgetreten. Bitte versuche es später erneut oder wende dich an dein Center."
        } else {
          this.submissionError = "Die Registrierung konnte nicht abgeschlossen werden. Bitte versuche es erneut oder wende dich an dein Center."
        }
      } finally {
        this.isSubmitting = false
      }
    },
    nextStep() {
      if (!this.stepFinished) return
      if (this.registrationFormStepper === 6) {
        this.register()
      } else {
        this.resetValidation()
        this.registrationFormStepper++
      }
    },
    previousStep() {
      if (this.registrationFormStepper <= 1 || this.isSubmitting) return
      this.resetValidation()
      this.registrationFormStepper--
    },
    async validateEmail() {
      if (!this.$refs.registerForm.validate()) return

      const requestId = ++this.emailValidationRequestId
      this.validatingEmail = true
      this.emailAlreadyUsed = false
      this.emailValidationError = ""

      try {
        const emailAlreadyUsed = await api.getEmailAlreadyUsed({ email: this.formData.email })
        if (requestId !== this.emailValidationRequestId) return

        this.emailAlreadyUsed = emailAlreadyUsed
        this.validatingEmail = this.emailAlreadyUsed ? false : "validated"
      } catch (error) {
        if (requestId !== this.emailValidationRequestId) return

        this.validatingEmail = false
        this.emailValidationError = "Die E-Mail-Adresse konnte gerade nicht geprüft werden. Bitte versuche es erneut."
      }
    },
    setRegisterForm() {
      this.formData = {
        email: "",
        consentDeclarationSignature: null,
        passwort: "",
        vorname: null,
        nachname: null,
        adresse_1: null,
        adresse_2: null,
        plz: null,
        stadt: null,
        land: "Schweiz",
        telefon: null,
        geschlecht: "",
        geburtstag: null,
        gewicht: null,
        groesse: null,
        bild: null,
        error: false,
        active: false,
        request: false,
        birthDayPickerActive: false,
        birthDayPickerState: "",
        errorMessage: "",
        rulesAccepted: false,
        newsletterAccepted: false,
        newsletterCenter: "Frauenfeld",
        eventId: null,
      }
    },
  },
}
</script>
